/**
 * DistanceGoalAnnotation - Independent panel for distance constraint annotations
 *
 * Wraps SpatialGoalEditor in an AnnotationPanel for independent save functionality.
 * Allows coaches to define distance-based constraints between body landmarks.
 *
 * This is Type 2 of 3 annotation types:
 * - Body Region: Highlight regions of interest on the body
 * - Distance Goal: Distance constraints between landmarks (this one)
 * - Movement Goal: Track landmark movement during phases
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import AnnotationPanel from './AnnotationPanel';
import SpatialGoalEditor from './SpatialGoalEditor';
import type { SpatialGoal, Phase } from '@/types/api';
import type { PoseData } from '@/utils/landmarkDetection';
import { getLandmarkLabel } from '@/utils/landmarkDetection';

interface DistanceGoalAnnotationProps {
  // Panel state
  isExpanded: boolean;
  onToggle: () => void;
  panelNumber?: number;
  totalPanels?: number;

  // Spatial goal state
  spatialGoal: SpatialGoal | undefined;
  onSpatialGoalChange: (goal: SpatialGoal | undefined) => void;

  // Pose data for landmark selection
  poseData?: PoseData | null;
  videoWidth?: number;
  videoHeight?: number;
  videoContainerRef?: React.RefObject<HTMLDivElement | null>;

  // Phase context (optional)
  phases?: Phase[];
  currentPhase?: Phase | null;

  // Save callback
  onSave: (goal: SpatialGoal, label: string, notes: string, importance: number) => Promise<void>;

  // Landmark selection callbacks (now managed by RegionAnnotator)
  onRequestSourceSelection?: (callback: (landmark: string) => void) => void;
  onRequestTargetSelection?: (callback: (landmark: string) => void) => void;

  // Status
  isSaved?: boolean;
  isSelectionActive?: boolean;

  className?: string;
}

export default function DistanceGoalAnnotation({
  isExpanded,
  onToggle,
  panelNumber = 2,
  totalPanels = 3,
  spatialGoal,
  onSpatialGoalChange,
  poseData,
  videoWidth = 1920,
  videoHeight = 1080,
  videoContainerRef,
  phases,
  currentPhase,
  onSave,
  onRequestSourceSelection,
  onRequestTargetSelection,
  isSaved = false,
  isSelectionActive = false,
  className,
}: DistanceGoalAnnotationProps) {
  // Local form state
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [importance, setImportance] = useState(50);
  const [isSaving, setIsSaving] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(false);

  // Auto-generate label from selected landmarks.
  // Reads the latest `label` via a ref (not a dependency) so this effect only
  // re-runs when the landmark selection changes, not on every keystroke in
  // the label field — depending on `label` directly would re-fire on every
  // edit and could snap a manually-cleared label back to the auto value.
  const labelRef = useRef(label);
  labelRef.current = label;

  useEffect(() => {
    if (spatialGoal?.source_landmark && spatialGoal?.target?.target_landmark) {
      const sourceName = getLandmarkLabel(spatialGoal.source_landmark);
      const targetName = getLandmarkLabel(spatialGoal.target.target_landmark);
      const currentLabel = labelRef.current;
      // Only auto-set if label is empty or matches a previous auto-generated pattern
      if (!currentLabel || currentLabel.includes(' → ') || currentLabel.includes(' to ')) {
        setLabel(`${sourceName} → ${targetName}`);
      }
    }
  }, [spatialGoal?.source_landmark, spatialGoal?.target?.target_landmark]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!spatialGoal?.source_landmark || !spatialGoal?.target?.target_landmark || !label.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(spatialGoal, label.trim(), notes.trim(), importance);

      // Clear form after successful save (but keep spatial goal visible)
      setLabel('');
      setNotes('');
      setImportance(50);
      setLocalIsSaved(true);

      // Clear spatial goal after successful save
      onSpatialGoalChange(undefined);

      // Reset saved indicator after delay
      setTimeout(() => setLocalIsSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save distance goal annotation:', error);
    } finally {
      setIsSaving(false);
    }
  }, [spatialGoal, label, notes, importance, onSave, onSpatialGoalChange]);

  // Can save when we have both landmarks selected and a label
  const hasCompleteSpatialGoal = !!(
    spatialGoal?.enabled &&
    spatialGoal?.source_landmark &&
    spatialGoal?.target?.target_landmark
  );
  const canSave = hasCompleteSpatialGoal && !!label.trim();

  // Handle spatial goal change - ensure it's enabled when user interacts
  const handleSpatialGoalChange = useCallback((goal: SpatialGoal | undefined) => {
    onSpatialGoalChange(goal);
  }, [onSpatialGoalChange]);

  return (
    <AnnotationPanel
      title="How far apart two body points are"
      subtitle="Measured at one moment in the movement, not over time."
      icon={<ArrowsRightLeftIcon className="w-5 h-5" />}
      accentColor="purple"
      panelNumber={panelNumber}
      totalPanels={totalPanels}
      isExpanded={isExpanded}
      onToggle={onToggle}
      label={label}
      onLabelChange={setLabel}
      notes={notes}
      onNotesChange={setNotes}
      importance={importance}
      onImportanceChange={setImportance}
      canSave={canSave}
      isSaving={isSaving}
      isSaved={localIsSaved || isSaved}
      onSave={handleSave}
      saveButtonText="Save Distance Check"
      className={cn(className, isSelectionActive && 'opacity-50 pointer-events-none')}
    >
      {/* Spatial Goal Editor */}
      <SpatialGoalEditor
        value={spatialGoal}
        onChange={handleSpatialGoalChange}
        hasPhases={!!(phases && phases.length > 0)}
        poseData={poseData}
        videoWidth={videoWidth}
        videoHeight={videoHeight}
        videoContainerRef={videoContainerRef}
        onRequestSourceSelection={onRequestSourceSelection}
        onRequestTargetSelection={onRequestTargetSelection}
      />

      {/* Phase Context Info */}
      {currentPhase && (
        <div className="mt-3 p-2 bg-violet-500/10 border border-violet-500/30 rounded-lg text-xs text-violet-400">
          <span className="font-medium">Current Phase:</span> {currentPhase.name}
          <span className="text-violet-400/70 ml-2">
            (frames {currentPhase.start_frame}-{currentPhase.end_frame})
          </span>
        </div>
      )}

      {/* Status message when no pose data */}
      {!poseData && (
        <div className="mt-3 p-3 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning">
          <strong>Note:</strong> Pose data not loaded yet. Visual landmark selection will be available once pose data is extracted from the video.
        </div>
      )}

      {/* Completion status */}
      {hasCompleteSpatialGoal && (
        <div className="mt-3 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-violet-400">
            <svg className="h-5 w-5 text-violet-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">
              Distance constraint configured
            </span>
          </div>
          <p className="text-xs text-violet-400/70 mt-1 ml-7">
            {getLandmarkLabel(spatialGoal!.source_landmark)} → {getLandmarkLabel(spatialGoal!.target!.target_landmark!)}
          </p>
        </div>
      )}
    </AnnotationPanel>
  );
}
